/**
 * Who put a medication on the list, and over what dates it was taken.
 *
 * ★★★ MED-PROVENANCE-LABEL-1 — THE CARD USED TO ANSWER "WHO ADDED THIS" TWICE, FROM TWO
 *   DIFFERENT RULES, AND THEY DISAGREED:
 *
 *     border : source === 'prescribed'                       -> green, else blue
 *     subline: source === 'prescribed' && med.prescribedBy   -> "Rx · Dr. X", else "Self-reported"
 *
 *   A prescribed row whose prescriber could not be resolved matched the first and not the
 *   second, so it rendered a DOCTOR-COLOURED CARD READING "Self-reported". An `md_entered`
 *   row — a doctor typing into the chart — matched NEITHER, so it was labelled as the
 *   patient's own report on a blue card. Wrong twice.
 *
 * ★★★ AND THE FILE ALREADY KNEW: `isDoctorSourced()` sat three lines above the subline with
 *   the correct predicate, used ONLY to stop the patient deleting the row. The app refused to
 *   let you delete a medication it was simultaneously telling you that you had reported.
 *
 * ★★ "Self-reported" IS NOT A NEUTRAL FALLBACK ON A MEDICAL RECORD — it is a claim about who
 *   said it. Falling back to it attributes a doctor's prescription to the patient. Same family
 *   as the invented stop reason D307 removed: when the answer is not known, the record must not
 *   supply a confident one. This degrades to "your doctor" and never to "Dr. Unknown".
 *
 * Pure: no React, no clock, no I/O, and NO `new Date()` — see the note on parseDateParts.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * ★★★ DATES ARE PARSED BY SPLITTING THE STRING, NEVER BY `new Date()`.
 *   `new Date('2026-08-03')` is UTC midnight, and the app runs in the patient's local zone —
 *   so west of Greenwich it renders as 2 Aug. A medication start date that moves by a day
 *   depending on where the patient is standing is the TZ-ONE-CLOCK family, and the fix is not
 *   to add a timezone argument but to never build a Date in the first place.
 *
 * Accepts `YYYY-MM-DD` and month-precision `YYYY-MM`; returns null for anything else.
 */
export function parseDateParts(iso) {
  if (typeof iso !== 'string') return null
  const m = iso.trim().match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/)
  if (!m) return null
  const month = Number(m[2]) - 1
  if (!(month >= 0 && month <= 11)) return null
  const day = m[3] ? Number(m[3]) : null
  if (day !== null && !(day >= 1 && day <= 31)) return null
  return { y: Number(m[1]), m: month, d: day }
}

const monthName = (i, months) => (Array.isArray(months) && months[i]) || MONTHS[i]

function formatParts(p, months) {
  if (!p) return ''
  return p.d ? `${p.d} ${monthName(p.m, months)} ${p.y}` : `${monthName(p.m, months)} ${p.y}`
}

/**
 * Who added this medication.
 *
 * ** `prescribed` and `md_entered` are ONE answer to the patient — "your doctor" — because only
 *    one of them is literally a prescription and the difference is not the patient's to act on.
 *    "Added by" is true of both; "Prescribed by" would not be.
 */
export function medicationProvenance(med) {
  const source = med && med.source
  const byDoctor = source === 'prescribed' || source === 'md_entered'
  if (!byDoctor) return { kind: 'self', doctorName: null }

  const pb = med && med.prescribedBy && typeof med.prescribedBy === 'object' ? med.prescribedBy : null
  const raw = pb ? (pb.doctorName || pb.name || '') : ''
  // strip a title the writer already included, so we never render "Dr. Dr. Abdou"
  const name = typeof raw === 'string' ? raw.trim().replace(/^dr\.?\s+/i, '').trim() : ''
  return { kind: 'doctor', doctorName: name || null }
}

/**
 * The span the medication was actually taken.
 *
 * ★★ HIS RULING: a past medication states a RANGE, not just when it ended. "Finished 12 Aug"
 *   answers half the question — the chart wants to say how long they were on it.
 *
 * ** An ACTIVE row has no end, so it reads "Since <start>". A row with no usable start reads
 *    nothing at all rather than inventing a boundary.
 *
 * Returns { kind: 'range' | 'since' | 'none', text }.
 */
export function medicationTimespan(med, months) {
  if (!med || typeof med !== 'object') return { kind: 'none', text: '' }
  const start = parseDateParts(med.startDate || med.start_date || med.dateStarted)
  const isPast = med.status === 'discontinued' || med.status === 'completed'

  if (!isPast) {
    // ★★★ v242.174.0 — AN ACTIVE ROW THAT WAS ONCE A COURSE SAYS SO. `original_end_date`
    //   is set by the server when a patient taps "Still taking it": the reopen clears
    //   `end_date`, and without this line the row afterwards reads exactly like a
    //   medication that was never a course at all. The clinically interesting fact is
    //   precisely the difference — a 10-day course was ordered and it is still being
    //   taken — so the one place it can be read is the line under the name.
    const planned = parseDateParts(med.originalEndDate || med.original_end_date)
    if (planned) {
      const since = start ? `Since ${formatParts(start, months)} \u00B7 ` : ''
      return { kind: 'resumed', text: `${since}was prescribed to end ${formatParts(planned, months)}` }
    }
    return start ? { kind: 'since', text: `Since ${formatParts(start, months)}` } : { kind: 'none', text: '' }
  }

  const end = parseDateParts(
    med.discontinuedAt || med.discontinued_at || med.endDate || med.end_date
  )
  if (!start && !end) return { kind: 'none', text: '' }
  if (!start) return { kind: 'since', text: `Until ${formatParts(end, months)}` }
  if (!end) return { kind: 'since', text: `Since ${formatParts(start, months)}` }

  // ** Collapse what the two dates share, so the range reads the way a person says it:
  //    "3 – 12 Aug 2026", not "3 Aug 2026 – 12 Aug 2026".
  const sameYear = start.y === end.y
  const sameMonth = sameYear && start.m === end.m
  let left
  if (sameMonth && start.d && end.d) left = String(start.d)
  else if (sameYear) left = start.d ? `${start.d} ${monthName(start.m, months)}` : monthName(start.m, months)
  else left = formatParts(start, months)
  return { kind: 'range', text: `${left} – ${formatParts(end, months)}` }
}
